import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { delayedEnterAnimation } from '@app/core/animations';
import { AuthState } from '@app/core/auth/states/auth.state';
import { AppState } from '@app/core/states/app.state';
import { LogEvent, SetEventProperties } from '@app/core/states/event-tracking.actions';
import { SetSidenavCollapsed, SetSidenavHovered } from '@app/ui/states/ui.actions';
import { UIState } from '@app/ui/states/ui.state';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { TranslateService } from '@ngx-translate/core';
import { Dispatch } from '@ngxs-labs/dispatch-decorator';
import { Store } from '@ngxs/store';
import { Subscription, combineLatest, fromEvent } from 'rxjs';
import { debounceTime, map } from 'rxjs/operators';

export interface MenuItem {
  label: string;
  icon: IconProp;
  route: string;
  isExternalURL: boolean;
  disabled?: boolean;
}

@Component({
  selector: 'rk-main-menu',
  templateUrl: './main-menu.component.html',
  styleUrls: ['./main-menu.component.scss'],
  standalone: false,
  animations: [delayedEnterAnimation],
})
export class MainMenuComponent implements OnInit, OnDestroy {
  menuItems: MenuItem[] = [];

  userRKDomain$ = this.store.select(AuthState.userRKDomain);
  isSuperUser$ = this.store.select(AuthState.isSuperUser);
  currentApp$ = this.store.select(AppState.currentApp);
  sidenavCollapsed$ = this.store.select(UIState.sidenavCollapsed);
  menuWidth$ = this.store.select(UIState.menuWidth);

  private readonly subscription: Subscription = new Subscription();
  private readonly COLLAPSE_BREAKPOINT = 1080;

  constructor(
    private readonly translate: TranslateService,
    private readonly router: Router,
    private readonly store: Store,
  ) {}

  ngOnInit() {
    this.subscription.add(
      combineLatest([this.userRKDomain$, this.currentApp$, this.isSuperUser$])
        .pipe(
          map(([domain, app, isSuperUser]) => {
            const hasApp = !!app?.appId;
            const menu = [
              {
                label: this.translate.instant('menu.dashboard'),
                icon: ['fas', 'gauge-high'] as IconProp,
                route: `/apps/${app?.appId}`,
                isExternalURL: false,
                disabled: !hasApp,
                trackingId: 'Statistics',
              },
              {
                label: this.translate.instant('menu.notifications'),
                icon: ['fas', 'comment'] as IconProp,
                route: `/notifications/${app?.appId}`,
                isExternalURL: false,
                disabled: !hasApp,
                trackingId: 'Notifications',
              },
              {
                label: this.translate.instant('menu.content'),
                icon: ['fas', 'palette'] as IconProp,
                route: hasApp
                  ? app.theme === '4'
                    ? `/simulator/${app.appId}`
                    : `${domain}/update-app-${app.appId}`
                  : '',
                isExternalURL: hasApp && app.theme !== '4',
                disabled: !hasApp,
                trackingId: 'Content',
              },
              {
                label: this.translate.instant('menu.settings'),
                icon: ['fas', 'cog'] as IconProp,
                route: hasApp
                  ? app.theme === '4'
                    ? `/update/${app.appId}`
                    : `${domain}/update-publication-${app.appId}`
                  : '',
                isExternalURL: hasApp && app.theme !== '4',
                disabled: !hasApp,
                trackingId: 'Settings',
              },
              {
                label: this.translate.instant('menu.publishing'),
                icon: ['fas', 'rocket'] as IconProp,
                route: `/publishing/${app?.appId}`,
                isExternalURL: false,
                disabled: !hasApp,
                trackingId: 'Publishing',
              },
            ];

            if (isSuperUser) {
              menu.push({
                label: 'Admin',
                icon: ['fas', 'user-shield'] as IconProp,
                route: '/admin',
                isExternalURL: false,
                disabled: false,
                trackingId: 'Admin',
              });
            }

            return menu;
          }),
        )
        .subscribe(menuItems => {
          this.menuItems = menuItems;
        }),
    );

    this.subscription.add(
      this.router.events.subscribe(event => {
        if (event && this.router.url.includes('simulator/')) {
          this.collapseMenu();
        }
      }),
    );

    this.subscription.add(
      fromEvent(window, 'resize')
        .pipe(debounceTime(100))
        .subscribe(() => {
          this.checkWindowWidth();
        }),
    );

    this.checkWindowWidth();
  }

  private checkWindowWidth() {
    const windowWidth = window.innerWidth;

    if (windowWidth < this.COLLAPSE_BREAKPOINT) {
      this.collapseMenu();
    } else if (!this.router.url.includes('simulator/')) {
      this.uncollapseMenu();
    }
  }

  onMenuItemClick(trackingId: string) {
    // event tracking
    this.store.dispatch(
      new SetEventProperties({
        name: `NavMain${trackingId}`,
        component: 'Navigation',
      }),
    );
    this.store.dispatch(new LogEvent('Navigation Clicked'));
  }

  get isNavCollapsed() {
    return (
      this.store.selectSnapshot(UIState.sidenavCollapsed) &&
      !this.store.selectSnapshot(UIState.sidenavHovered)
    );
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  onMenuMouseEnter() {
    this.store.dispatch(new SetSidenavHovered(true));
  }

  onMenuMouseLeave() {
    this.store.dispatch(new SetSidenavHovered(false));
  }

  @Dispatch()
  collapseMenu() {
    return new SetSidenavCollapsed(true);
  }

  @Dispatch()
  uncollapseMenu() {
    return new SetSidenavCollapsed(false);
  }
}
